(() => {
if (window.SaludSubscriptionCartSync?.version === 'subscription-gifts-v16') return;
const subscriptionCart = {
  version: 'subscription-gifts-v16',
  lastCart: null,
  tieredCollectionIds: ['496420290788', '496041001188', '413363241188'],
  bundleCollectionIds: ['413363241188'],
  collectionIdsByProduct: new Map(),
  productDataByHandle: new Map(),
  productDataCache: new Map(),
  planRefreshRequestedHandles: new Set(),
  bundlePlanSyncRunning: false,
  bundlePlanSyncPromise: null,
  bundlePlanSyncQueued: false,
  bundlePlanSyncTimer: null,
  bundleCheckoutLockActive: false,
  subscriptionGiftSyncRunning: false,
  subscriptionGiftSyncPromise: null,
  subscriptionGiftSyncError: null,
  subscriptionGiftSyncQueued: false,
  subscriptionGiftSyncTimer: null,
  subscriptionGiftAddInFlight: false,
  checkoutLockCount: 0,
  pendingCartRequests: new Set(),
  externalCartMutationVersion: 0,
  cartSyncPauseCount: 0,
  pauseCartSync() {
    this.cartSyncPauseCount += 1;
    clearTimeout(this.subscriptionGiftSyncTimer);
    clearTimeout(this.bundlePlanSyncTimer);
    this.subscriptionGiftSyncTimer = null;
    this.bundlePlanSyncTimer = null;
    if (this.bundleCheckoutLockActive && !this.bundlePlanSyncRunning) {
      this.bundleCheckoutLockActive = false;
      this.unlockCheckout();
    }
    const running = [this.subscriptionGiftSyncPromise, this.bundlePlanSyncPromise, ...this.pendingCartRequests].filter(Boolean);
    return Promise.allSettled(running).then(() => running.length > 0);
  },

  resumeCartSync(cart) {
    this.cartSyncPauseCount = Math.max(0, this.cartSyncPauseCount - 1);
    if (this.cartSyncPauseCount) return;
    this.subscriptionGiftSyncQueued = false;
    this.bundlePlanSyncQueued = false;
    this.scheduleSubscriptionGiftSync(cart);
    this.scheduleBundleSellingPlanSync();
  },
  offers() {
    const offers = window.SaludSubscriptionConfig?.offers;
    return [...(Array.isArray(offers) ? offers : [
      { quantity: 1, orderDiscount: 20, defaultFrequency: 30, gifts: [] },
      { quantity: 2, orderDiscount: 25, defaultFrequency: 30, gifts: [] },
      { quantity: 3, orderDiscount: 30, defaultFrequency: 30, gifts: [] },
    ])]
      .filter((offer) => Number(offer.quantity) > 0)
      .sort((firstOffer, secondOffer) =>
        Number(firstOffer.quantity) - Number(secondOffer.quantity)
      );
  },

  offerForQuantity(quantity) {
    const requestedQuantity = Math.max(1, Number(quantity) || 1);
    const offers = this.offers();
    const eligibleOffers = offers.filter(
      (offer) => Number(offer.quantity) <= requestedQuantity
    );

    return eligibleOffers[eligibleOffers.length - 1] || offers[0] || null;
  },

  defaultFrequency() {
    return String(this.offerForQuantity(1)?.defaultFrequency || 30);
  },

  isSubscriptionGift(item) {
    return String(item?.properties?._subscription_gift || '').toLowerCase() === 'true';
  },

  isManagedSubscriptionGift(item) {
    return this.isSubscriptionGift(item)
      || String(item?.properties?._bundle_builder_gift || '').toLowerCase() === 'true';
  },

  subscriptionGiftLines(offer, items = []) {
    const gifts = new Map();
    const subscriptionProductIds = items
      .filter((item) => !this.isManagedSubscriptionGift(item) && item.properties?._gift !== 'true'
        && this.currentSellingPlanId(item) && Number(item.quantity) > 0)
      .map((item) => item.product_id || item.product?.id);

    (offer?.gifts || []).forEach((gift) => {
      if (!window.SaludSubscriptionConfig.isGiftEligible(gift, subscriptionProductIds)) return;
      const rawVariantId = window.SaludSubscriptionConfig.giftVariantId(gift);
      if (!rawVariantId) return;

      const variantId = Number(rawVariantId);
      if (!Number.isFinite(variantId)) return;

      // A gift is unique by its variant. A duplicate reference in the
      // metaobject or a repeated add-to-cart request must never increase its
      // cart quantity.
      gifts.set(variantId, 1);
    });

    return [...gifts].map(([id, quantity]) => ({
      id,
      quantity,
      properties: {
        _subscription_gift: 'true',
        _subscription_gift_offer: String(offer?.orderDiscount || offer?.quantity || ''),
        _subscription_gift_offer_id: String(offer?.id || offer?.handle || ''),
      },
    }));
  },

  subscriptionGiftQuantity(items) {
    return items.reduce((total, item) => {
      if (this.isManagedSubscriptionGift(item) || !this.isSubscriptionCartItem(item)) return total;

      return total + Number(item.quantity || 0);
    }, 0);
  },

  hasMatchingSubscriptionGifts(items, giftLines, offer) {
    const gifts = items.filter((item) => this.isManagedSubscriptionGift(item));
    const actualQuantities = new Map();
    const expectedQuantities = new Map(giftLines.map((gift) => [String(gift.id), gift.quantity]));
    const offerKey = String(offer?.orderDiscount || offer?.quantity || '');
    const offerId = String(offer?.id || offer?.handle || '');

    gifts.forEach((gift) => {
      const variantId = String(gift.variant_id || gift.variant?.id || '');
      actualQuantities.set(variantId, (actualQuantities.get(variantId) || 0) + Number(gift.quantity || 0));
    });

    if (actualQuantities.size !== expectedQuantities.size) return false;

    return [...expectedQuantities].every(([variantId, quantity]) => (
      actualQuantities.get(variantId) === quantity &&
      gifts
        .filter((gift) => String(gift.variant_id || gift.variant?.id || '') === variantId)
        .every((gift) => (
          String(gift.properties?._subscription_gift_offer || '') === offerKey &&
          String(gift.properties?._subscription_gift_offer_id || '') === offerId
        ))
    ));
  },

  scheduleSubscriptionGiftSync(cart = null) {
    clearTimeout(this.subscriptionGiftSyncTimer);
    this.subscriptionGiftSyncTimer = null;
    if (this.cartSyncPauseCount) {
      this.subscriptionGiftSyncQueued = true;
      return;
    }
    this.subscriptionGiftSyncTimer = setTimeout(() => this.syncSubscriptionGifts(cart), 150);
  },

  async currentCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Unable to load cart: ${response.status}`);

    this.lastCart = await response.json();
    return this.lastCart;
  },

  subscriptionLineIdentity(item, plans = []) {
    const sellingPlanId = this.currentSellingPlanId(item);
    if (!sellingPlanId || !item.key || this.isManagedSubscriptionGift(item)
      || item.properties?._gift === 'true' || item.parent_relationship
      || item.item_components?.length) return null;
    const properties = Object.entries(item.properties || {})
      .filter(([key]) => key !== 'Discount')
      .sort(([first], [second]) => first.localeCompare(second));
    const plan = plans.find((candidate) => String(candidate.id) === sellingPlanId)
      || this.sellingPlanForItem(item, this.productDataCache.get(this.productHandle(item)))
      || item.selling_plan_allocation?.selling_plan;
    const frequency = this.planFrequency(plan);
    return JSON.stringify([
      String(item.variant_id || item.id),
      frequency ? `frequency:${frequency}` : `plan:${sellingPlanId}`,
      Number(item.final_price ?? item.price ?? 0),
      properties,
    ]);
  },

  async mergeSubscriptionLines(cart, plansByVariant = new Map()) {
    const groups = new Map();
    for (const item of cart?.items || []) {
      const identity = this.subscriptionLineIdentity(item, plansByVariant.get(String(item.variant_id || item.id)) || []);
      if (!identity) continue;
      if (!groups.has(identity)) groups.set(identity, []);
      groups.get(identity).push(item);
    }

    const updates = {};
    for (const items of groups.values()) {
      if (items.length < 2 || new Set(items.map((item) => item.key)).size !== items.length) continue;
      updates[items[0].key] = items.reduce((total, item) => total + Number(item.quantity || 0), 0);
      for (const item of items.slice(1)) updates[item.key] = 0;
    }
    if (!Object.keys(updates).length) return cart;

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
      body: JSON.stringify({ updates }),
    });
    const mergedCart = await response.json();
    if (!response.ok || mergedCart.status) {
      throw new Error(mergedCart.description || 'Unable to merge subscription items.');
    }
    this.lastCart = mergedCart;
    return mergedCart;
  },

  giftStateForCart(cart) {
    const subscriptionQuantity = this.subscriptionGiftQuantity(cart?.items || []);
    const offer = subscriptionQuantity > 0
      ? this.offerForQuantity(subscriptionQuantity)
      : null;

    return {
      subscriptionQuantity,
      offer,
      giftLines: offer ? this.subscriptionGiftLines(offer, cart.items || []) : [],
    };
  },

  async removeAllSubscriptionGifts(cart) {
    let nextCart = cart;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const gifts = nextCart?.items?.filter((item) => this.isManagedSubscriptionGift(item)) || [];
      if (!gifts.length) return nextCart;
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
          body: JSON.stringify({ updates: Object.fromEntries(gifts.map((gift) => [gift.key, 0])) }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Unable to remove subscription gifts.');
        nextCart = result;
      } catch (error) {
        if (attempt === 2) throw error;
        nextCart = await this.currentCart();
      }
    }
    if (!nextCart.items?.some((item) => this.isManagedSubscriptionGift(item))) return nextCart;
    throw new Error('Unable to remove all subscription gifts.');
  },

  async clearAndVerifySubscriptionGifts(cart) {
    let nextCart = cart;

    for (let pass = 0; pass < 5; pass += 1) {
      nextCart = await this.removeAllSubscriptionGifts(nextCart);
      if (!nextCart.items?.some((item) => this.isManagedSubscriptionGift(item))) {
        return nextCart;
      }
    }

    throw new Error('Unable to clear subscription gifts before rebuild');
  },

  syncSubscriptionGifts(cart = null, options = {}) {
    clearTimeout(this.subscriptionGiftSyncTimer);
    this.subscriptionGiftSyncTimer = null;
    if (this.subscriptionGiftSyncPromise) {
      this.subscriptionGiftSyncQueued = true;
      return this.subscriptionGiftSyncPromise;
    }

    this.subscriptionGiftSyncPromise = this.runSubscriptionGiftSync(cart, options).finally(() => {
      this.subscriptionGiftSyncPromise = null;
    });
    return this.subscriptionGiftSyncPromise;
  },

  giftError() {
    return new Error(window.SaludSubscriptionConfig?.giftSyncError || 'We could not confirm your free gifts. Please refresh your bag and try again.');
  },

  async reconcileSubscriptionGifts(cart) {
    const limit = Math.max(20, (cart.items?.length || 0) * 2);
    for (let attempt = 0; attempt < limit; attempt += 1) {
      const state = this.giftStateForCart(cart);
      const gifts = (cart.items || []).filter((item) => this.isManagedSubscriptionGift(item));
      const desired = new Map(state.giftLines.map((gift) => [String(gift.id), gift]));
      const retained = new Map();
      const updates = {};
      for (const gift of gifts) {
        const id = String(gift.variant_id || gift.id);
        if (!desired.has(id) || retained.has(id) || this.currentSellingPlanId(gift)) updates[gift.key] = 0;
        else retained.set(id, gift);
      }
      const missing = state.giftLines.filter((gift) => !retained.has(String(gift.id)));
      const drawer = document.querySelector('salud-cart-drawer-v2');
      if (missing.length && drawer?.prepareCheckoutGiftOrder) {
        return drawer.prepareCheckoutGiftOrder(cart, state.giftLines);
      }
      if (Object.keys(updates).length) {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
          body: JSON.stringify({ updates }),
        });
        cart = await response.json();
        if (!response.ok || cart.status || !Array.isArray(cart.items)) throw this.giftError();
        continue;
      }
      const changed = state.giftLines.find((gift) => {
        const item = retained.get(String(gift.id));
        return item && (Number(item.quantity) !== gift.quantity
          || Object.entries(gift.properties).some(([key, value]) => item.properties?.[key] !== value));
      });
      if (changed) {
        cart = await this.changeCartLine(retained.get(String(changed.id)), changed.quantity, undefined, changed.properties);
        if (cart.status || !Array.isArray(cart.items)) throw this.giftError();
        continue;
      }
      if (!missing.length) return cart;
      this.subscriptionGiftAddInFlight = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
          body: JSON.stringify({ items: missing }),
        });
        if (!response.ok) throw this.giftError();
        cart = await this.currentCart();
      } finally {
        this.subscriptionGiftAddInFlight = false;
      }
    }
    throw this.giftError();
  },

  subscriptionItems(cart) {
    return (cart.items || []).filter(item => this.currentSellingPlanId(item)
      && !this.isManagedSubscriptionGift(item) && item.properties?._gift !== 'true');
  },

  async subscriptionPlanChanges(cart) {
    const items = this.subscriptionItems(cart);
    const quantity = items.reduce((total, item) => total + Number(item.quantity), 0);
    const changes = await Promise.all(items.map(async item => {
      const frequency = await this.frequencyForItem(item);
      if (!frequency) throw this.giftError();
      const plan = await this.sellingPlanId(item, quantity, frequency);
      if (!plan) throw this.giftError();
      const properties = this.propertiesForQuantity(item, quantity);
      if (this.currentSellingPlanId(item) !== String(plan) || item.properties?.Discount !== properties.Discount) {
        return { item, plan, properties };
      }
      return null;
    }));
    return changes.filter(Boolean);
  },

  async normalizeSubscriptionPlans(cart) {
    const limit = Math.max(20, (cart.items?.length || 0) * 2);
    for (let attempt = 0; attempt < limit; attempt += 1) {
      this.lastCart = cart;
      const changes = await this.subscriptionPlanChanges(cart);
      if (!changes.length) return cart;
      const { item, plan, properties } = changes[0];
      cart = await this.changeCartLine(item, item.quantity, plan, properties);
    }
    throw this.giftError();
  },

  async assertCheckoutReady(cart) {
    const state = this.giftStateForCart(cart);
    const minimum = Math.max(1, Number(window.SaludSubscriptionConfig?.minimumSubscriptionQuantity) || 1);
    if (state.subscriptionQuantity > 0 && state.subscriptionQuantity < minimum) throw this.giftError();
    if (!this.hasMatchingSubscriptionGifts(cart.items || [], state.giftLines, state.offer)
      || (await this.subscriptionPlanChanges(cart)).length) throw this.giftError();
    const gifts = (cart.items || []).filter(item => this.isManagedSubscriptionGift(item) || item.properties?._gift === 'true');
    if (gifts.some(item => typeof item.final_line_price !== 'number' || item.final_line_price !== 0
      || this.currentSellingPlanId(item))) throw this.giftError();
    return cart;
  },

  async prepareSubscriptionGiftOrder(cart) {
    await this.assertCheckoutReady(cart);
    const drawer = document.querySelector('salud-cart-drawer-v2');
    if (!drawer?.prepareCheckoutGiftOrder) return cart;
    const orderedCart = await drawer.prepareCheckoutGiftOrder(cart);
    if (orderedCart !== cart) await this.assertCheckoutReady(orderedCart);
    this.lastCart = orderedCart;
    return orderedCart;
  },

  async prepareCheckout() {
    for (let attempt = 0; attempt < 5 && this.pendingCartRequests.size; attempt += 1) {
      await Promise.allSettled([...this.pendingCartRequests]);
    }
    if (this.pendingCartRequests.size) throw this.giftError();
    const cart = await this.flushSubscriptionGifts();
    const quantity = this.subscriptionItems(cart).reduce((total, item) => total + Number(item.quantity), 0);
    const minimum = Math.max(1, Number(window.SaludSubscriptionConfig?.minimumSubscriptionQuantity) || 1);
    if (!cart.items?.length || (quantity > 0 && quantity < minimum)) throw this.giftError();
    return cart;
  },

  needsGiftInventory(cart) {
    return this.subscriptionGiftQuantity(cart?.items || []) > 0
      || (cart?.items || []).some((item) => this.isManagedSubscriptionGift(item));
  },

  async flushSubscriptionGifts() {
    const inventoryRefresh = this.needsGiftInventory(this.lastCart)
      ? Promise.allSettled([window.SaludSubscriptionConfig.refreshGiftInventory()])
      : null;
    let cart = await this.currentCart();
    if (this.needsGiftInventory(cart)) {
      const [result] = await (inventoryRefresh
        || Promise.allSettled([window.SaludSubscriptionConfig.refreshGiftInventory()]));
      if (result.status === 'rejected') throw result.reason;
    }
    try {
      await this.assertCheckoutReady(cart);
      this.subscriptionGiftSyncError = null;
      return cart;
    } catch {
    }
    for (let attempt = 0; attempt < 3; attempt += 1) {
      cart = await this.syncSubscriptionGifts(cart, { inventoryRefreshed: true });
      clearTimeout(this.subscriptionGiftSyncTimer);
      this.subscriptionGiftSyncTimer = null;
      this.subscriptionGiftSyncQueued = false;
      if (this.subscriptionGiftSyncError) throw this.subscriptionGiftSyncError;

      cart = await this.currentCart();
      const giftState = this.giftStateForCart(cart);
      if (this.hasMatchingSubscriptionGifts(cart.items || [], giftState.giftLines, giftState.offer)) return this.assertCheckoutReady(cart);
    }

    throw new Error('Unable to verify all subscription gifts in your cart. Please try again.');
  },

  async runSubscriptionGiftSync(sourceCart = null, { inventoryRefreshed = false } = {}) {
    this.subscriptionGiftSyncRunning = true;
    this.subscriptionGiftSyncQueued = false;
    this.subscriptionGiftSyncError = null;
    this.lockCheckout();

    try {
      if (this.bundlePlanSyncPromise) {
        await this.bundlePlanSyncPromise;
        sourceCart = null;
      }
      let cart = sourceCart || await this.currentCart();
      this.lastCart = cart;
      if (!cart?.items) return;

      const normalizedCart = await this.normalizeSubscriptionPlans(cart);
      if (normalizedCart !== cart) this.refreshCart(normalizedCart);
      cart = normalizedCart;
      const mergedCart = await this.mergeSubscriptionLines(cart);
      if (mergedCart !== cart) {
        cart = mergedCart;
        this.refreshCart(cart);
      }

      if (!inventoryRefreshed && this.needsGiftInventory(cart)) {
        await window.SaludSubscriptionConfig.refreshGiftInventory();
      }
      const giftState = this.giftStateForCart(cart);

      if (this.hasMatchingSubscriptionGifts(cart.items, giftState.giftLines, giftState.offer)) {
        const orderedCart = await this.prepareSubscriptionGiftOrder(cart);
        if (orderedCart !== cart) this.refreshCart(orderedCart);
        return orderedCart;
      }

      cart = await this.reconcileSubscriptionGifts(cart);

      // A cart mutation can land during refresh. Queue one more
      // canonical read instead of leaving a partially rebuilt gift set.
      if (!this.hasMatchingSubscriptionGifts(cart.items, giftState.giftLines, giftState.offer)) {
        this.subscriptionGiftSyncQueued = true;
      }

      cart = await this.prepareSubscriptionGiftOrder(cart);
      this.refreshCart(cart);
      return cart;
    } catch (error) {
      this.subscriptionGiftSyncError = error;
      console.error('Unable to synchronize subscription gifts.', error);
    } finally {
      this.subscriptionGiftSyncRunning = false;
      this.unlockCheckout();

      if (this.subscriptionGiftSyncQueued) {
        this.subscriptionGiftSyncQueued = false;
        this.scheduleSubscriptionGiftSync();
      }
      if (this.bundlePlanSyncQueued) this.scheduleBundleSellingPlanSync();
    }
  },

  getCollectionIds(item) {
    const product = item?.product || {};
    const ids = [];

    if (Array.isArray(product.collection_ids)) ids.push(...product.collection_ids);
    if (Array.isArray(product.collections_ids)) ids.push(...product.collections_ids);
    if (Array.isArray(item?.collection_ids)) ids.push(...item.collection_ids);

    if (Array.isArray(product.collections)) {
      product.collections.forEach((collection) => {
        ids.push(typeof collection === 'object' && collection !== null ? collection.id : collection);
      });
    }

    const productId = String(item?.product_id || product.id || '');
    const collectionIds = ids.map(String);

    if (productId && collectionIds.length) {
      this.collectionIdsByProduct.set(productId, collectionIds);
    }

    return collectionIds.length
      ? collectionIds
      : this.collectionIdsByProduct.get(productId) || [];
  },

  isTieredProduct(item) {
    const tags = [item?.product?.tags, item?.tags]
      .flatMap((tagSource) => {
        if (Array.isArray(tagSource)) return tagSource;
        return typeof tagSource === 'string' ? tagSource.split(',') : [];
      })
      .map((tag) => String(tag).trim().toLowerCase());

    return (
      this.getCollectionIds(item).some((id) => this.tieredCollectionIds.includes(id)) ||
      tags.includes('bundle') ||
      this.hasTieredSellingPlans(item)
    );
  },

  hasTieredSellingPlans(item) {
    const product = this.productDataCache.get(this.productHandle(item));
    const configuredDiscounts = new Set(
      this.offers()
        .map((offer) => Number(offer.orderDiscount))
        .filter(Number.isFinite)
    );
    const variantId = String(item?.variant_id || item?.variant?.id || '');
    const variants = variantId
      ? (product?.variants || []).filter((variant) => String(variant.id) === variantId)
      : product?.variants || [];
    const matchingDiscounts = new Set();

    variants.forEach((variant) => {
      (variant.selling_plan_allocations || []).forEach((allocation) => {
        const discount = this.planDiscount(allocation, variant);
        if (configuredDiscounts.has(discount)) matchingDiscounts.add(discount);
      });
    });

    return matchingDiscounts.size > 1;
  },

  isBundleProduct(item) {
    return this.getCollectionIds(item).some((id) => this.bundleCollectionIds.includes(id));
  },

  isTieredSubscriptionItem(item) {
    return Boolean(
      item &&
        this.isTieredProduct(item) &&
        (this.currentSellingPlanId(item) || item?.product?.subscription)
    );
  },

  isSubscriptionCartItem(item) {
    return Boolean(
      item &&
        (
          this.currentSellingPlanId(item) ||
          item?.selling_plan_allocation ||
          item?.selling_plan ||
          item?.product?.subscription ||
          item?.product?.subscription_frequency
        )
    );
  },

  isBundleSubscriptionItem(item) {
    return Boolean(item && this.isBundleProduct(item) && this.currentSellingPlanId(item));
  },

  currentSellingPlanId(item) {
    return String(
      item?.selling_plan_allocation?.selling_plan?.id ||
        item?.selling_plan_allocation?.selling_plan_id ||
        item?.selling_plan_id ||
        item?.product?.selling_plan_id ||
        item?.product?.subscription?.selling_plan_id ||
        ''
    );
  },

  sellingPlanForItem(item, product) {
    const sellingPlanId = this.currentSellingPlanId(item);
    if (!sellingPlanId || !product) return null;

    return (product.selling_plan_groups || [])
      .flatMap((group) => group.selling_plans || [])
      .find((plan) => String(plan.id) === sellingPlanId) || null;
  },

  productHandle(item) {
    const directHandle = item?.product?.handle || item?.handle;
    if (directHandle) return String(directHandle);

    const productUrl = item?.product_url || item?.url || '';
    const match = String(productUrl).match(/\/products\/([^/?#]+)/);

    return match?.[1] || '';
  },

  async productData(item) {
    const handle = this.productHandle(item);
    if (!handle) return null;

    if (!this.productDataByHandle.has(handle)) {
      const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load product ${handle}.`);
          return response.json();
        })
        .then((product) => {
          this.productDataCache.set(handle, product);
          return product;
        })
        .catch((error) => {
          this.productDataByHandle.delete(handle);
          console.error('Unable to load subscription plans.', error);
          return null;
        });

      this.productDataByHandle.set(handle, request);
    }

    return this.productDataByHandle.get(handle);
  },

  requestFrequencyRefresh(item) {
    const handle = this.productHandle(item);
    if (!handle || this.productDataCache.has(handle) || this.planRefreshRequestedHandles.has(handle)) return;

    this.planRefreshRequestedHandles.add(handle);
    this.productData(item).then((product) => {
      if (product && this.lastCart) this.refreshCart(this.lastCart);
      if (!product) this.planRefreshRequestedHandles.delete(handle);
    });
  },

  preloadProductData() {
    const items = this.lastCart?.items || [];
    const productItems = items.filter((item) => this.productHandle(item));
    const uncachedHandles = new Set(productItems
      .map((item) => this.productHandle(item))
      .filter((handle) => !this.productDataCache.has(handle)));

    return Promise.all(
      productItems.map((item) => this.productData(item))
    ).then((products) => {
      if ([...uncachedHandles].some((handle) => this.productDataCache.has(handle)) && this.lastCart) {
        this.refreshCart(this.lastCart);
      }

      return products;
    });
  },

  setCheckoutDisabled(disabled) {
    document
      .querySelectorAll(
        '#CartDrawer [name="checkout"], #main-cart-footer [name="checkout"]'
      )
      .forEach((button) => {
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      });
  },

  syncCheckoutState() {
    this.setCheckoutDisabled(this.checkoutLockCount > 0 || this.pendingCartRequests.size > 0 || Boolean(this.subscriptionGiftSyncTimer) || Boolean(this.subscriptionGiftSyncError) || this.lastCart?.items?.length === 0);
    document.dispatchEvent(new CustomEvent('salud:checkout-state', { detail: { error: this.subscriptionGiftSyncError } }));
  },

  lockCheckout() {
    this.checkoutLockCount += 1;
    this.setCheckoutDisabled(true);
  },

  unlockCheckout() {
    this.checkoutLockCount = Math.max(0, this.checkoutLockCount - 1);

    this.syncCheckoutState();
  },

  planFrequency(plan) {
    const values = [
      ...(plan?.options || []).map((option) => typeof option === 'string' ? option : option?.value),
      plan?.name,
    ];
    const planText = values.join(' ');
    const deliveryInterval = planText.match(/(?:every\s+)?(\d+)\s*days?\b/i);
    if (deliveryInterval) return deliveryInterval[1];

    const match = planText.match(/\d+/g);

    return match?.length ? match[match.length - 1] : '';
  },

  planDiscount(allocation, variant) {
    const regularPrice = Number(allocation.compare_at_price || variant?.price || 0);
    const planPrice = Number(allocation.price || 0);

    if (!regularPrice || planPrice > regularPrice) return null;

    return Math.round(((regularPrice - planPrice) / regularPrice) * 100);
  },

  frequency(item) {
    const cachedPlan = this.sellingPlanForItem(
      item,
      this.productDataCache.get(this.productHandle(item))
    );
    const cachedPlanFrequency = this.planFrequency(cachedPlan);
    if (cachedPlanFrequency) return cachedPlanFrequency;
    const planName =
      item?.selling_plan_allocation?.selling_plan?.name ||
      item?.selling_plan_allocation?.name ||
      '';
    const planFrequency = this.planFrequency({ name: planName });
    if (planFrequency) return planFrequency;

    return this.normalizeFrequency(item?.product?.subscription_frequency) || this.defaultFrequency();
  },

  async frequencyForItem(item) {
    const product = await this.productData(item);
    const sellingPlan = this.sellingPlanForItem(item, product);
    if (sellingPlan) return this.planFrequency(sellingPlan);
    const planName =
      item?.selling_plan_allocation?.selling_plan?.name ||
      item?.selling_plan_allocation?.name ||
      '';
    const frequency = this.planFrequency({ name: planName });
    if (frequency) return frequency;

    return '';
  },

  availableFrequencies(item) {
    const product = this.productDataCache.get(this.productHandle(item));
    const variantId = String(item?.variant_id || item?.variant?.id || '');
    const variant = product?.variants?.find((candidate) => String(candidate.id) === variantId);
    const quantity = this.isBundleSubscriptionItem(item)
      ? this.bundleTotalQuantity(this.lastCart?.items || [item])
      : item?.quantity;
    const discount = Number(this.offerForQuantity(quantity)?.orderDiscount);
    const offer = this.offerForQuantity(quantity);
    const currentFrequency = this.frequency(item);

    // Keep the selected value present while product plans are loading, or if
    // loading fails. Vue clears a select when its value has no matching option.
    if (!variant || !Number.isFinite(discount)) {
      this.requestFrequencyRefresh(item);
      return window.SaludSubscriptionConfig.isFrequencyAllowed(currentFrequency, offer, quantity) ? [currentFrequency] : [];
    }

    const plansById = new Map(
      (product.selling_plan_groups || [])
        .flatMap((group) => group.selling_plans || [])
        .map((plan) => [String(plan.id), plan])
    );
    const frequencies = (variant.selling_plan_allocations || [])
      .filter((allocation) => this.planDiscount(allocation, variant) === discount)
      .map((allocation) => this.planFrequency(plansById.get(String(allocation.selling_plan_id))))
      .filter((frequency) => window.SaludSubscriptionConfig.isFrequencyAllowed(frequency, offer, quantity));

    if (window.SaludSubscriptionConfig.isFrequencyAllowed(currentFrequency, offer, quantity)) frequencies.push(currentFrequency);
    return [...new Set(frequencies)].sort((first, second) => Number(first) - Number(second));
  },

  subscriptionDiscount(item) {
    const discount = this.offerForQuantity(item?.quantity)?.orderDiscount;

    return Number.isFinite(Number(discount)) ? `${Number(discount)}%` : '';
  },

  subscriptionButtonLabel(item, fallbackLabel) {
    if (!this.isTieredProduct(item)) return fallbackLabel;

    const discount = this.subscriptionDiscount(item);
    if (!discount) return fallbackLabel;

    if (/\d+\s*%/.test(fallbackLabel)) {
      return fallbackLabel.replace(/\d+\s*%/g, discount);
    }

    return `SUBSCRIBE & SAVE ${discount}`;
  },

  switchToSubscription(item) {
    this.changeItem(item, item.quantity, this.defaultFrequency());
  },

  changeQuantity(item, quantity) {
    const nextQuantity = Math.max(1, Number(quantity) || 1);
    return this.changeItem(item, nextQuantity, null, !this.currentSellingPlanId(item));
  },

  changeFrequency(item, frequency) {
    this.changeItem(item, item.quantity, frequency);
  },

  async changeItem(item, quantity, frequency, onetime = false) {
    this.lockCheckout();

    try {
      const targetQuantity = this.isBundleSubscriptionItem(item)
        ? this.bundleTotalQuantity(this.lastCart?.items || [item]) - Number(item.quantity) + Number(quantity)
        : quantity;
      const targetFrequency = onetime ? '' : frequency || await this.frequencyForItem(item);
      const sellingPlanId = onetime ? null : targetFrequency && await this.sellingPlanId(item, targetQuantity, targetFrequency);
      if (!onetime && !sellingPlanId) return;

      const cart = await this.changeCartLine(
        item,
        quantity,
        sellingPlanId,
        onetime ? item.properties || {} : this.propertiesForQuantity(item, targetQuantity)
      );

      this.lastCart = cart;
      const synchronizedCart = await this.syncSubscriptionGifts(cart);
      this.refreshCart(synchronizedCart || cart);
      this.scheduleBundleSellingPlanSync();
    } catch (error) {
      console.error('Unable to update the subscription item.', error);
    } finally {
      this.unlockCheckout();
    }
  },

  async changeCartLine(item, quantity, sellingPlanId, properties) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Salud-Cart-Sync': 'true' },
      body: JSON.stringify({
        id: item.key,
        quantity,
        selling_plan: sellingPlanId,
        properties,
      }),
    });

    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

    return response.json();
  },

  async sellingPlanId(item, quantity, frequency) {
    const offer = this.offerForQuantity(quantity);
    const product = await this.productData(item);
    const variantId = String(item?.variant_id || item?.variant?.id || '');
    const variant = product?.variants?.find((candidate) => String(candidate.id) === variantId);
    const targetFrequency = this.normalizeFrequency(frequency || offer?.defaultFrequency);
    const targetDiscount = Number(offer?.orderDiscount);

    if (!variant || !targetFrequency || !Number.isFinite(targetDiscount)) return null;

    const plansById = new Map(
      (product.selling_plan_groups || []).flatMap((group) => group.selling_plans || [])
        .map((plan) => [String(plan.id), plan])
    );

    const allowedAllocations = (variant.selling_plan_allocations || []).filter((candidate) => {
      const plan = plansById.get(String(candidate.selling_plan_id));

      return (
        window.SaludSubscriptionConfig.isFrequencyAllowed(this.planFrequency(plan), offer, quantity) &&
        this.planDiscount(candidate, variant) === targetDiscount
      );
    });
    const nearestFrequency = window.SaludSubscriptionConfig.nearestFrequency(
      allowedAllocations.map((candidate) => this.planFrequency(plansById.get(String(candidate.selling_plan_id)))), targetFrequency,
    );
    const allocation = allowedAllocations.find((candidate) => (
      this.planFrequency(plansById.get(String(candidate.selling_plan_id))) === nearestFrequency
    ));

    return allocation ? String(allocation.selling_plan_id) : null;
  },

  propertiesForQuantity(item, quantity) {
    const properties = Object.assign({}, item.properties || {});
    const discount = this.subscriptionDiscount({ quantity });

    if (discount) properties.Discount = discount;

    return properties;
  },

  bundleTotalQuantity(items) {
    return items.reduce((total, item) => {
      if (!this.isBundleSubscriptionItem(item)) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  },

  async bundleTargetState(item, totalQuantity) {
    const offer = this.offerForQuantity(totalQuantity);
    const frequency = await this.frequencyForItem(item);
    const discount = Number.isFinite(Number(offer?.orderDiscount))
      ? `${Number(offer.orderDiscount)}%`
      : '';

    return {
      sellingPlanId: frequency ? await this.sellingPlanId(item, offer?.quantity, frequency) : null,
      discount,
    };
  },

  async lineNeedsBundleUpdate(item, totalQuantity) {
    const target = await this.bundleTargetState(item, totalQuantity);
    if (!target.sellingPlanId) return false;

    return (
      this.currentSellingPlanId(item) !== String(target.sellingPlanId) ||
      item?.properties?.Discount !== target.discount
    );
  },

  scheduleBundleSellingPlanSync() {
    clearTimeout(this.bundlePlanSyncTimer);
    this.bundlePlanSyncTimer = null;
    if (this.cartSyncPauseCount || this.subscriptionGiftSyncRunning) {
      this.bundlePlanSyncQueued = true;
      if (this.bundleCheckoutLockActive && !this.bundlePlanSyncRunning) {
        this.bundleCheckoutLockActive = false;
        this.unlockCheckout();
      }
      return;
    }

    if (!this.bundleCheckoutLockActive) {
      this.bundleCheckoutLockActive = true;
      this.lockCheckout();
    }

    this.bundlePlanSyncTimer = setTimeout(() => this.syncBundleSellingPlans(), 50);
  },

  syncBundleSellingPlans() {
    if (this.subscriptionGiftSyncPromise) return this.subscriptionGiftSyncPromise;
    if (this.bundlePlanSyncPromise) {
      this.bundlePlanSyncQueued = true;
      return this.bundlePlanSyncPromise;
    }
    this.bundlePlanSyncPromise = this.runBundleSellingPlanSync().finally(() => {
      this.bundlePlanSyncPromise = null;
    });
    return this.bundlePlanSyncPromise;
  },

  async runBundleSellingPlanSync() {
    this.bundlePlanSyncRunning = true;
    this.bundlePlanSyncQueued = false;
    try {
      let cart = await this.currentCart();
      let wasChanged = false;

      if (!cart?.items?.length) return;

      const normalizedCart = await this.normalizeSubscriptionPlans(cart);
      wasChanged = normalizedCart !== cart;
      cart = normalizedCart;

      const mergedCart = await this.mergeSubscriptionLines(cart);
      if (mergedCart !== cart) {
        cart = mergedCart;
        wasChanged = true;
      }

      if (wasChanged) this.refreshCart(cart);
    } catch (error) {
      console.error('Unable to synchronize bundle selling plans.', error);
    } finally {
      this.bundlePlanSyncRunning = false;

      if (this.bundlePlanSyncQueued) {
        this.bundlePlanSyncQueued = false;
        this.scheduleBundleSellingPlanSync();
      } else if (this.bundleCheckoutLockActive) {
        this.bundleCheckoutLockActive = false;
        this.unlockCheckout();
      }
    }
  },

  normalizeFrequency(value) {
    const match = String(value || '').match(/\d+/g);
    return match?.length ? match[match.length - 1] : '';
  },

  refreshCart(cart) {
    this.lastCart = cart;
    if (this.cartSyncPauseCount) return;
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart, subscriptionSync: true } }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'subscription-cart-sync', cartData: cart });
    }

    requestAnimationFrame(() => this.syncCheckoutState());
  },
};
// Keep subscription and bundle selling plan updates in the theme.
window.SaludSubscriptionCartSync = subscriptionCart;

const syncCart = (event) => {
  subscriptionCart.syncCheckoutState();
  if (event?.detail?.subscriptionSync) return;
  subscriptionCart.scheduleSubscriptionGiftSync();
  subscriptionCart.scheduleBundleSellingPlanSync();
};
const handleCartMutation = () => {
  subscriptionCart.scheduleSubscriptionGiftSync();
  subscriptionCart.scheduleBundleSellingPlanSync();
};
document.addEventListener('cart:refresh', syncCart);
document.addEventListener('subscription-gifts:change', () => subscriptionCart.scheduleSubscriptionGiftSync());

// Catch completed Shopify cart requests from theme sections and product forms.
const saludFetch = window.fetch;
window.fetch = function saludCartFetch(...args) {
  const request = args[0];
  const url = typeof request === 'string' ? request : request?.url;
  const internalSync = new Headers(args[1]?.headers || request?.headers).get('X-Salud-Cart-Sync') === 'true';
  const isCartMutation = !internalSync && /\/cart\/(?:add|change|update)(?:\.js)?(?:[?#]|$)/.test(String(url || ''));
  const response = saludFetch.apply(this, args);

  if (isCartMutation) {
    subscriptionCart.externalCartMutationVersion += 1;
    subscriptionCart.pendingCartRequests.add(response);
    subscriptionCart.syncCheckoutState();
    const complete = () => {
      subscriptionCart.pendingCartRequests.delete(response);
      handleCartMutation();
      subscriptionCart.syncCheckoutState();
    };
    response.then(complete, complete);
  }

  return response;
};

const saludXhrOpen = XMLHttpRequest.prototype.open;
const saludXhrSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.open = function saludCartOpen(method, url, ...args) {
  this.saludCartMutation = /\/cart\/(?:add|change|update)(?:\.js)?(?:[?#]|$)/.test(String(url || ''));
  return saludXhrOpen.call(this, method, url, ...args);
};
XMLHttpRequest.prototype.send = function saludCartSend(...args) {
  if (this.saludCartMutation) {
    subscriptionCart.externalCartMutationVersion += 1;
    let complete;
    const pending = new Promise(resolve => { complete = resolve; });
    subscriptionCart.pendingCartRequests.add(pending);
    subscriptionCart.syncCheckoutState();
    this.addEventListener('loadend', () => {
      subscriptionCart.pendingCartRequests.delete(pending);
      complete();
      handleCartMutation();
      subscriptionCart.syncCheckoutState();
    }, { once: true });
  }

  return saludXhrSend.apply(this, args);
};

subscriptionCart.currentCart().then(() => {
  subscriptionCart.preloadProductData();
  subscriptionCart.scheduleSubscriptionGiftSync();
  subscriptionCart.scheduleBundleSellingPlanSync();
}).catch(error => {
  subscriptionCart.subscriptionGiftSyncError = error;
  subscriptionCart.syncCheckoutState();
});
})();
